import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { pathOr } from 'ramda'
import { PureComponent } from 'react'

import { Input, Select, Time } from '@/app/component/atom/form'
import TimezonePicker from '@/app/component/atom/timezone-picker'
import Tooltip from '@/app/component/atom/tooltip'
import { CAMPAIGN_CHANGE_DELAY } from '@/app/module/campaigns/definitions'
import { getDaysOfWeek, getDaysOfWeekValue } from '@/app/module/campaigns/helpers'
import { Delta } from '@/app/module/campaigns/types'
import {
  daysOfTheWeek,
  daysOfTheWeekOrder,
  getSchedule,
  processSchedule,
  ScheduleValue,
} from '@/app/service/util/schedule'
import { toTimezoneOptions } from '@/app/service/util/timezone'
import { CountryTimezone, SelectOption, TimezoneWithCountryCodeType } from '@/app/types'

const ERROR_DELAY = 'Delay has to be bigger than one minute.'

type DelayValue = Delta['value'] | string

type DelayItem = {
  delta?: Partial<Delta>
}

type Props<T extends DelayItem> = {
  countriesTimezones: CountryTimezone[]
  countryCode?: string
  helpText?: string
  isFirst?: boolean
  item: T
  message?: string
  published?: boolean
  schedule?: boolean
  timezone?: string
  timezones: TimezoneWithCountryCodeType

  changeHandler: (item: T) => void
  setLoading: (loading: boolean) => void
}

type State = {
  country?: string
  dayOfWeek: string
  daysOfWeek: string[]
  schedule: ScheduleValue
  time: string
  timeout?: ReturnType<typeof setTimeout>
  timezone?: string
  timezones: SelectOption<string>[]
  unit: Delta['unit']
  value: DelayValue
  valueError: string
}

const defaultDelta = {
  unit: 'day',
  value: 0,
} satisfies Pick<Delta, 'unit' | 'value'>

const withDefaultDelta = <T extends Partial<Delta>>(delta?: T) => ({
  ...delta,
  unit: delta?.unit ?? defaultDelta.unit,
  value: delta?.value ?? defaultDelta.value,
})

const units: SelectOption<Delta['unit']>[] = [
  {
    label: 'immediately',
    value: 'immediate',
  },
  {
    label: 'seconds',
    value: 'second',
  },
  {
    label: 'minutes',
    value: 'minute',
  },
  {
    label: 'hours',
    value: 'hour',
  },
  {
    label: 'days',
    value: 'day',
  },
  {
    label: 'weeks',
    value: 'week',
  },
]

const scheduleFirstList = (unit: Delta['unit'] | undefined, value: DelayValue, schedule = false) => [
  {
    label: schedule ? 'same as initial call' : 'same as subscription time',
    value: 'same',
    disabled: !schedule && (unit === 'day' || unit === 'week') && (value === 0 || value === '0'),
  },
  {
    label: 'specific time of the day',
    value: 'specific',
  },
]

const scheduleList = (unit: Delta['unit'] | undefined, value: DelayValue, schedule = false) => [
  {
    label: schedule ? 'same as initial call' : 'same as previous message',
    value: 'same',
    disabled: !schedule && (unit === 'day' || unit === 'week') && (value === 0 || value === '0'),
  },
  ...(schedule
    ? [
        {
          label: 'same as previous retry call',
          value: 'previous',
        },
      ]
    : []),
  {
    label: 'specific time of the day',
    value: 'specific',
  },
]

export default class DripDelay<T extends DelayItem> extends PureComponent<Props<T>, State> {
  constructor(props: Props<T>) {
    super(props)
    const delta = withDefaultDelta(props.item.delta)
    const countryCode = delta.country ? delta.country : props.countryCode
    const hasZeroSpecificDelay = !props.schedule && delta.value === 0 && (delta.unit === 'day' || delta.unit === 'week')
    this.state = {
      value: delta.value,
      valueError: '',
      unit: delta.value === 0 && props.isFirst && delta.unit !== 'week' && !delta.time ? 'immediate' : delta.unit,
      time: delta.time ? delta.time : '09:00',
      schedule: hasZeroSpecificDelay ? 'specific' : getSchedule(delta),
      country: countryCode,
      timezone: delta.timezone ? delta.timezone : props.timezone,
      dayOfWeek: '',
      daysOfWeek: getDaysOfWeekValue(delta),
      timezones: toTimezoneOptions(pathOr([], [countryCode ?? '', 'tzs'], props.timezones)),
    }
  }

  componentDidUpdate(prevProps: Readonly<Props<T>>) {
    if (prevProps.timezones !== this.props.timezones) {
      const delta = withDefaultDelta(this.props.item.delta)
      const countryCode = delta.country ? delta.country : this.props.countryCode
      this.setState({
        timezones: toTimezoneOptions(pathOr([], [countryCode ?? '', 'tzs'], this.props.timezones)),
      })
    }
  }

  debouncedChange(item: T) {
    const { changeHandler, setLoading } = this.props

    if (this.state.timeout) {
      clearTimeout(this.state.timeout)
    }
    this.props.setLoading(true)
    this.setState({
      timeout: setTimeout(() => {
        changeHandler(item)
        setLoading(false)
        this.setState((state) => {
          clearTimeout(state.timeout)
          return {
            timeout: undefined,
          }
        })
      }, CAMPAIGN_CHANGE_DELAY),
    })
  }

  render() {
    const {
      item,
      isFirst,
      countriesTimezones,
      timezones,
      published = false,
      message = 'send this message',
      helpText = 'after the previous message',
      schedule = false,
    } = this.props

    const itemDelta = withDefaultDelta(item.delta)
    const hasSpecific = this.state.unit === 'day' || this.state.unit === 'week'

    return (
      <div className="campaign-item-content-delay">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
              {message}
              {published && (
                <div
                  style={{
                    float: 'right',
                  }}
                >
                  <Tooltip title="Changing this delay will not immediately affect current subscriptions. See the support article for details.">
                    <Icon color="error">info</Icon>
                  </Tooltip>
                </div>
              )}
            </Typography>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Icon style={{ margin: '0.5rem 0.5rem 0 0', color: 'rgba(0, 0, 0, 0.54)' }}>timer</Icon>
              <div style={{ width: this.state.unit === 'immediate' ? '0' : '3rem' }}>
                {this.state.unit !== 'immediate' && (
                  <Input
                    type="number"
                    name="delay-value"
                    value={String(this.state.value)}
                    error={this.state.valueError}
                    inputProps={{
                      min: hasSpecific ? 0 : 1,
                    }}
                    onChange={({ value }) => {
                      const min = hasSpecific ? 0 : 1
                      const numericValue = Number(value)
                      const error = Number.isNaN(numericValue) || numericValue < min ? ERROR_DELAY : ''
                      const nextValue = !error ? numericValue : min
                      this.setState({
                        value: !error ? value : min,
                        schedule: numericValue === 0 ? 'specific' : this.state.schedule,
                      })
                      this.debouncedChange({
                        ...item,
                        delta: {
                          ...itemDelta,
                          ...(hasSpecific &&
                            numericValue === 0 && {
                              time: itemDelta.time || this.state.time,
                              dayOfWeek: itemDelta.dayOfWeek || this.state.dayOfWeek,
                              daysOfWeek: itemDelta.daysOfWeek || getDaysOfWeek(this.state.daysOfWeek),
                              timezone: itemDelta.timezone || this.state.timezone,
                            }),
                          value: nextValue,
                        },
                      })
                    }}
                  />
                )}
              </div>
              <div style={{ width: this.state.unit === 'immediate' ? '9rem' : '6rem' }}>
                <Select
                  data-testid="delay-unit"
                  value={this.state.unit}
                  values={units}
                  name="delay-unit"
                  onChange={({ value }) => {
                    let newValue = itemDelta.value
                    if (value === 'immediate') {
                      newValue = 0
                    } else if (value !== 'day' && value !== 'week') {
                      newValue = Math.max(1, Number(itemDelta.value))
                    }
                    this.setState({
                      unit: value,
                      value: newValue,
                    })
                    const { relative } = processSchedule(item, this.state.schedule, value, schedule)

                    this.debouncedChange({
                      ...item,
                      delta: {
                        ...itemDelta,
                        unit: value === 'immediate' ? 'minute' : value,
                        value: newValue,
                        time: value !== 'week' && value !== 'day' ? '' : itemDelta.time || '',
                        dayOfWeek: value !== 'week' ? '' : itemDelta.dayOfWeek,
                        daysOfWeek: value !== 'week' ? {} : getDaysOfWeek(this.state.daysOfWeek),
                        relative,
                      },
                    })
                  }}
                />
              </div>
            </div>
            <div>
              <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                {isFirst && !schedule && 'after the contact is subscribed to the campaign'}
                {(schedule || !isFirst) && helpText}
              </Typography>
            </div>
          </div>
          {hasSpecific && (
            <div>
              <div>
                <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                  at this time of the day
                </Typography>
                <Select
                  data-testid="delay-schedule"
                  value={this.state.schedule}
                  name="delay-schedule"
                  values={
                    isFirst
                      ? scheduleFirstList(this.state.unit, this.state.value, schedule)
                      : scheduleList(this.state.unit, this.state.value, schedule)
                  }
                  onChange={({ value }) => {
                    this.setState({
                      schedule: value,
                    })

                    const { isRelative, canSetDaysOfWeek, relative } = processSchedule(
                      item,
                      value,
                      this.state.unit,
                      schedule,
                    )

                    this.debouncedChange({
                      ...item,
                      delta: {
                        ...itemDelta,
                        time: isRelative ? '' : this.state.time,
                        dayOfWeek: !canSetDaysOfWeek ? '' : this.state.dayOfWeek,
                        daysOfWeek: !canSetDaysOfWeek ? {} : getDaysOfWeek(this.state.daysOfWeek),
                        country: isRelative ? '' : this.state.country,
                        timezone: isRelative ? '' : this.state.timezone,
                        relative,
                      },
                    })
                  }}
                />
              </div>
              {this.state.schedule === 'specific' && (
                <Row>
                  {this.state.unit === 'week' && (
                    <div style={{ width: '6rem' }}>
                      <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                        Days
                      </Typography>
                      <Select
                        data-testid="delay-days-of-week"
                        name="delay-days-of-week"
                        multiple={true}
                        value={this.state.daysOfWeek}
                        values={daysOfTheWeek}
                        onChange={({ value = [] }) => {
                          const sortedValue = value.length
                            ? value.sort((a, b) => daysOfTheWeekOrder[a] - daysOfTheWeekOrder[b])
                            : this.state.daysOfWeek

                          this.setState({
                            daysOfWeek: sortedValue,
                          })
                          this.debouncedChange({
                            ...item,
                            delta: {
                              ...itemDelta,
                              dayOfWeek: '',
                              daysOfWeek: sortedValue.reduce((obj, v) => ({ ...obj, [v]: true }), {}),
                            },
                          })
                        }}
                      />
                    </div>
                  )}
                  <div style={{ width: '6rem' }}>
                    <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                      Time
                    </Typography>
                    <Time
                      label=""
                      name="delay-time"
                      value={this.state.time}
                      onChange={({ value }) => {
                        this.setState({
                          time: value,
                        })
                        this.debouncedChange({
                          ...item,
                          delta: {
                            ...itemDelta,
                            time: value,
                          },
                        })
                      }}
                    />
                  </div>
                  <div style={{ width: '18rem' }}>
                    <TimezonePicker
                      countriesTimezones={countriesTimezones}
                      countryCode={this.state.country}
                      name="delay"
                      timezone={this.state.timezone}
                      timezones={timezones}
                      onChange={(timezone, country) => {
                        this.setState({
                          timezone,
                        })
                        this.debouncedChange({
                          ...item,
                          delta: {
                            ...itemDelta,
                            country,
                            timezone,
                          },
                        })
                      }}
                    />
                  </div>
                </Row>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
}

const Row = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(),
}))
